/**
 * Reliability Selectors - Phase 14
 * 
 * Query helpers for reliability data across merchant/location/terminal scopes.
 */

import { listQueueItems, listSyncLog } from '../offline/db';
import { aggregateAllIncidents } from '../offlineAudit/incidentStore';
import { 
  buildReliabilitySummary, 
  computeReliabilityStatus,
  RELIABILITY_STATUS 
} from './reliabilityModel';

// ==================== MAIN SELECTORS ====================

/**
 * Get merchant-level reliability summary
 * @param {Object} filters - { merchantId, dateFrom, dateTo }
 * @returns {Promise<Object>}
 */
export async function getMerchantReliabilitySummary(filters = {}) {
  const { merchantId, dateFrom, dateTo } = filters;
  
  const [incidents, queueItems, syncLogs] = await Promise.all([
    aggregateAllIncidents({ includeCompleted: true }),
    listQueueItems(),
    listSyncLog(1000)
  ]);
  
  // Filter by merchant if specified
  let filteredIncidents = incidents;
  let filteredQueue = queueItems;
  
  if (merchantId) {
    filteredIncidents = incidents.filter(i => i.merchantId === merchantId);
    filteredQueue = queueItems.filter(i => i.merchantId === merchantId);
  }
  
  return buildReliabilitySummary({
    entityType: 'merchant',
    entityId: merchantId || 'all',
    entityName: merchantId ? `Merchant ${merchantId}` : 'All Merchants',
    incidents: filteredIncidents,
    queueItems: filteredQueue,
    syncLogs,
    dateFrom,
    dateTo
  });
}

/**
 * Get location-level reliability summary
 * @param {Object} filters - { locationId, merchantId, dateFrom, dateTo }
 * @returns {Promise<Object>}
 */
export async function getLocationReliabilitySummary(filters = {}) {
  const { locationId, merchantId, dateFrom, dateTo } = filters;
  
  const [incidents, queueItems, syncLogs] = await Promise.all([
    aggregateAllIncidents({ includeCompleted: true }),
    listQueueItems(),
    listSyncLog(1000)
  ]);
  
  // Filter by location
  let filteredIncidents = incidents.filter(i => i.locationId === locationId);
  let filteredQueue = queueItems.filter(i => i.locationId === locationId);
  
  if (merchantId) {
    filteredIncidents = filteredIncidents.filter(i => i.merchantId === merchantId);
    filteredQueue = filteredQueue.filter(i => i.merchantId === merchantId);
  }
  
  return buildReliabilitySummary({
    entityType: 'location',
    entityId: locationId,
    entityName: `Location ${locationId}`,
    incidents: filteredIncidents,
    queueItems: filteredQueue,
    syncLogs,
    dateFrom,
    dateTo
  });
}

/**
 * Get terminal-level reliability summary
 * @param {Object} filters - { terminalId, locationId, merchantId, dateFrom, dateTo }
 * @returns {Promise<Object>}
 */
export async function getTerminalReliabilitySummary(filters = {}) {
  const { terminalId, locationId, merchantId, dateFrom, dateTo } = filters;
  
  const [incidents, queueItems, syncLogs] = await Promise.all([
    aggregateAllIncidents({ includeCompleted: true }),
    listQueueItems(),
    listSyncLog(1000)
  ]);
  
  // Filter by terminal
  let filteredIncidents = incidents.filter(i => i.terminalId === terminalId);
  let filteredQueue = queueItems.filter(i => i.terminalId === terminalId);
  
  if (locationId) {
    filteredIncidents = filteredIncidents.filter(i => i.locationId === locationId);
    filteredQueue = filteredQueue.filter(i => i.locationId === locationId);
  }
  
  if (merchantId) {
    filteredIncidents = filteredIncidents.filter(i => i.merchantId === merchantId);
    filteredQueue = filteredQueue.filter(i => i.merchantId === merchantId);
  }
  
  return buildReliabilitySummary({
    entityType: 'terminal',
    entityId: terminalId,
    entityName: `Terminal ${terminalId}`,
    incidents: filteredIncidents,
    queueItems: filteredQueue,
    syncLogs,
    dateFrom,
    dateTo
  });
}

/**
 * Get reliability trends over time
 * @param {Object} filters - { merchantId, locationId, terminalId, days }
 * @returns {Promise<Object>}
 */
export async function getReliabilityTrends(filters = {}) {
  const { merchantId, locationId, terminalId, days = 7 } = filters;
  
  const incidents = await aggregateAllIncidents({ includeCompleted: true });
  const syncLogs = await listSyncLog(2000);
  
  // Apply entity filters
  let filteredIncidents = incidents;
  if (terminalId) {
    filteredIncidents = incidents.filter(i => i.terminalId === terminalId);
  } else if (locationId) {
    filteredIncidents = incidents.filter(i => i.locationId === locationId);
  } else if (merchantId) {
    filteredIncidents = incidents.filter(i => i.merchantId === merchantId);
  }
  
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  
  const trends = {
    daily: [],
    totalIncidents: filteredIncidents.length,
    avgIncidentsPerDay: 0,
    trend: 'stable' // improving, stable, degrading
  };
  
  // Build daily breakdown
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = now - (i * dayMs);
    const dayEnd = dayStart + dayMs;
    const date = new Date(dayStart).toISOString().slice(0, 10);
    
    const dayIncidents = filteredIncidents.filter(inc => 
      inc.occurredAt >= dayStart && inc.occurredAt < dayEnd
    );
    
    const daySyncs = syncLogs.filter(log => 
      log.syncedAt >= dayStart && log.syncedAt < dayEnd
    );
    
    const successSyncs = daySyncs.filter(l => l.result === 'success').length;
    const syncRate = daySyncs.length > 0 ? (successSyncs / daySyncs.length * 100) : 100;
    
    trends.daily.push({
      date,
      incidents: dayIncidents.length,
      criticalIncidents: dayIncidents.filter(i => i.severity === 'critical').length,
      blockedPayments: dayIncidents.filter(i => 
        i.domain === 'payments' && (i.status === 'failed' || i.status === 'conflict')
      ).length,
      syncSuccessRate: syncRate
    });
  }
  
  // Calculate average and trend
  const totalDaily = trends.daily.reduce((sum, d) => sum + d.incidents, 0);
  trends.avgIncidentsPerDay = totalDaily / days;
  
  // Compare first half to second half for trend
  const halfPoint = Math.floor(days / 2);
  const firstHalf = trends.daily.slice(0, halfPoint);
  const secondHalf = trends.daily.slice(halfPoint);
  
  const firstAvg = firstHalf.reduce((s, d) => s + d.incidents, 0) / firstHalf.length;
  const secondAvg = secondHalf.reduce((s, d) => s + d.incidents, 0) / secondHalf.length;
  
  if (secondAvg < firstAvg * 0.8) {
    trends.trend = 'improving';
  } else if (secondAvg > firstAvg * 1.2) {
    trends.trend = 'degrading';
  }
  
  return trends;
}

/**
 * Get repeated issue entities (locations/terminals with recurring problems)
 * @param {Object} filters - { merchantId, minOccurrences }
 * @returns {Promise<Object>}
 */
export async function getRepeatedIssueEntities(filters = {}) {
  const { merchantId, minOccurrences = 3 } = filters;
  
  const incidents = await aggregateAllIncidents({ includeCompleted: true });
  
  let filtered = incidents;
  if (merchantId) {
    filtered = incidents.filter(i => i.merchantId === merchantId);
  }
  
  // Count by location
  const locationCounts = {};
  const terminalCounts = {};
  
  for (const inc of filtered) {
    if (inc.locationId) {
      if (!locationCounts[inc.locationId]) {
        locationCounts[inc.locationId] = { id: inc.locationId, count: 0, critical: 0, blocked: 0 };
      }
      locationCounts[inc.locationId].count++;
      if (inc.severity === 'critical') locationCounts[inc.locationId].critical++;
      if (inc.domain === 'payments' && inc.status === 'failed') locationCounts[inc.locationId].blocked++;
    }
    
    if (inc.terminalId) {
      if (!terminalCounts[inc.terminalId]) {
        terminalCounts[inc.terminalId] = { 
          id: inc.terminalId, 
          locationId: inc.locationId,
          count: 0, 
          critical: 0, 
          blocked: 0 
        };
      }
      terminalCounts[inc.terminalId].count++;
      if (inc.severity === 'critical') terminalCounts[inc.terminalId].critical++;
      if (inc.domain === 'payments' && inc.status === 'failed') terminalCounts[inc.terminalId].blocked++;
    }
  }
  
  // Filter by minimum occurrences and sort
  const repeatedLocations = Object.values(locationCounts)
    .filter(l => l.count >= minOccurrences)
    .sort((a, b) => b.count - a.count);
  
  const repeatedTerminals = Object.values(terminalCounts)
    .filter(t => t.count >= minOccurrences)
    .sort((a, b) => b.count - a.count);
  
  return {
    locations: repeatedLocations,
    terminals: repeatedTerminals,
    totalRepeatedLocations: repeatedLocations.length,
    totalRepeatedTerminals: repeatedTerminals.length
  };
}

/**
 * Get blocked payment reliability metrics
 * @param {Object} filters
 * @returns {Promise<Object>}
 */
export async function getBlockedPaymentReliability(filters = {}) {
  const { merchantId, locationId, terminalId, dateFrom, dateTo } = filters;
  
  const incidents = await aggregateAllIncidents({ includeCompleted: true });
  const syncLogs = await listSyncLog(1000);
  
  // Filter for payment incidents
  let paymentIncidents = incidents.filter(i => i.domain === 'payments');
  
  if (terminalId) {
    paymentIncidents = paymentIncidents.filter(i => i.terminalId === terminalId);
  } else if (locationId) {
    paymentIncidents = paymentIncidents.filter(i => i.locationId === locationId);
  } else if (merchantId) {
    paymentIncidents = paymentIncidents.filter(i => i.merchantId === merchantId);
  }
  
  if (dateFrom) {
    paymentIncidents = paymentIncidents.filter(i => i.occurredAt >= dateFrom);
  }
  if (dateTo) {
    paymentIncidents = paymentIncidents.filter(i => i.occurredAt <= dateTo);
  }
  
  const blocked = paymentIncidents.filter(i => i.status === 'failed' || i.status === 'conflict');
  const resolved = paymentIncidents.filter(i => i.status === 'resolved');
  
  // Payment sync logs
  const paymentTypes = ['POS_PAYMENT_CASH', 'POS_PAYMENT_CARD'];
  const paymentLogs = syncLogs.filter(l => paymentTypes.includes(l.type));
  const successLogs = paymentLogs.filter(l => l.result === 'success');
  
  return {
    totalPaymentIncidents: paymentIncidents.length,
    blockedCount: blocked.length,
    resolvedCount: resolved.length,
    recoveryRate: paymentIncidents.length > 0 ? (resolved.length / paymentIncidents.length * 100) : 100,
    syncSuccessRate: paymentLogs.length > 0 ? (successLogs.length / paymentLogs.length * 100) : 100,
    unresolvedBlocked: blocked.length
  };
}

/**
 * Get resolution time metrics
 * @param {Object} filters
 * @returns {Promise<Object>}
 */
export async function getResolutionTimeMetrics(filters = {}) {
  const { merchantId, locationId, dateFrom, dateTo } = filters;
  
  const incidents = await aggregateAllIncidents({ includeCompleted: true });
  
  // Filter for resolved incidents with resolution time
  let resolved = incidents.filter(i => i.resolvedAt && i.occurredAt);
  
  if (locationId) {
    resolved = resolved.filter(i => i.locationId === locationId);
  } else if (merchantId) {
    resolved = resolved.filter(i => i.merchantId === merchantId);
  }
  
  if (dateFrom) {
    resolved = resolved.filter(i => i.resolvedAt >= dateFrom);
  }
  if (dateTo) {
    resolved = resolved.filter(i => i.resolvedAt <= dateTo);
  }
  
  if (resolved.length === 0) {
    return {
      averageMs: 0,
      medianMs: 0,
      p90Ms: 0,
      minMs: 0,
      maxMs: 0,
      count: 0
    };
  }
  
  const times = resolved.map(i => i.resolvedAt - i.occurredAt).sort((a, b) => a - b);
  const sum = times.reduce((s, t) => s + t, 0);
  
  return {
    averageMs: Math.round(sum / times.length),
    medianMs: times[Math.floor(times.length / 2)],
    p90Ms: times[Math.floor(times.length * 0.9)],
    minMs: times[0],
    maxMs: times[times.length - 1],
    count: times.length
  };
}

/**
 * Get reliability status breakdown across all entities
 * @param {Object} filters - { merchantId }
 * @returns {Promise<Object>}
 */
export async function getReliabilityStatusBreakdown(filters = {}) {
  const { merchantId } = filters;
  
  const incidents = await aggregateAllIncidents({ includeCompleted: true });
  
  let filtered = incidents;
  if (merchantId) {
    filtered = incidents.filter(i => i.merchantId === merchantId);
  }
  
  // Group by location and compute status for each
  const locationIncidents = {};
  for (const inc of filtered) {
    if (inc.locationId) {
      if (!locationIncidents[inc.locationId]) {
        locationIncidents[inc.locationId] = [];
      }
      locationIncidents[inc.locationId].push(inc);
    }
  }
  
  const breakdown = {
    [RELIABILITY_STATUS.HEALTHY]: 0,
    [RELIABILITY_STATUS.WATCH]: 0,
    [RELIABILITY_STATUS.AT_RISK]: 0,
    [RELIABILITY_STATUS.CRITICAL]: 0
  };
  
  const locationStatuses = [];
  
  for (const [locationId, locIncidents] of Object.entries(locationIncidents)) {
    const summary = buildReliabilitySummary({
      entityType: 'location',
      entityId: locationId,
      incidents: locIncidents,
      queueItems: [],
      syncLogs: []
    });
    
    breakdown[summary.reliabilityStatus]++;
    locationStatuses.push({
      locationId,
      status: summary.reliabilityStatus,
      score: summary.reliabilityScore,
      incidentCount: locIncidents.length
    });
  }
  
  return {
    breakdown,
    locationStatuses: locationStatuses.sort((a, b) => a.score - b.score),
    totalLocations: Object.keys(locationIncidents).length
  };
}

export default {
  getMerchantReliabilitySummary,
  getLocationReliabilitySummary,
  getTerminalReliabilitySummary,
  getReliabilityTrends,
  getRepeatedIssueEntities,
  getBlockedPaymentReliability,
  getResolutionTimeMetrics,
  getReliabilityStatusBreakdown
};
